import React from 'react'
import { cloneDeep } from 'lodash'

import Box from 'components_new/atoms/Box'
import Table from 'components_new/atoms/Table'
import TableBody from 'components_new/atoms/Table/TableBody'
import TableCell from 'components_new/atoms/Table/TableCell'
import TableContainer from 'components_new/atoms/Table/TableContainer'
import TableRow from 'components_new/atoms/Table/TableRow'

import TrendText from 'components_new/molecules/TrendText'

import {
  KeyFigureVisualizationType,
  TCoreWidgetFullyLoaded
} from 'types/GlobalWidget'

import { parseValue, TParsedKpiValues } from '../utils'
import { translateStatus } from 'utils/enumTranslator'

interface ListContainerProps {
  data: TParsedKpiValues[]
  interactive: boolean
  scaleFactor: number
  widget: TCoreWidgetFullyLoaded
  onClick: (option: TParsedKpiValues) => void
  onKpiClick?: (event: React.MouseEvent, option: TParsedKpiValues) => void
}

const ListContainer = (props: ListContainerProps) => {
  const { data, interactive, onClick, onKpiClick, scaleFactor, widget } = props

  const isPublic = location.pathname.includes('public')

  let options = cloneDeep(data)
  let textColor: string | undefined = undefined

  if (
    widget.settings.key_figure_visualization_type ===
    KeyFigureVisualizationType.FOCUS
  ) {
    options = options.slice(1)
    textColor = 'text.secondary'
  }

  if (options.length === 0) {
    return null
  }

  const showComparativePeriod = Boolean(
    widget.settings.comparative_period.selected.value
  )

  const fontSize = `${1 * scaleFactor}rem`
  const listPadding = `${8 * scaleFactor}px`

  return (
    <TableContainer sx={{ overflow: 'visible' }}>
      <Table size={'small'}>
        <TableBody>
          {options.map((option) => {
            const clickAvailable =
              interactive &&
              !isPublic &&
              (onKpiClick != null || option.canShowDetails)

            return (
              <TableRow
                key={option.id}
                onClick={
                  clickAvailable
                    ? (e) =>
                        onKpiClick ? onKpiClick(e, option) : onClick(option)
                    : () => {}
                }
                sx={{
                  cursor: clickAvailable ? 'pointer' : undefined,
                  '&:hover': clickAvailable
                    ? {
                        '.SegmentLabel': {
                          textDecoration: 'underline'
                        },
                        '.ValueLabel': {
                          textDecoration: 'underline'
                        }
                      }
                    : undefined
                }}
              >
                <TableCell
                  className="SegmentLabel"
                  scope="row"
                  size="small"
                  sx={{
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100%',
                    maxWidth: 0,
                    padding: `${listPadding} 0px`,
                    color: textColor,
                    fontSize
                  }}
                >
                  {option.title}
                </TableCell>
                <TableCell
                  className="ValueLabel"
                  scope="row"
                  sx={{
                    width: '0px',
                    whiteSpace: 'normal',
                    minWidth: 0,
                    padding: `${listPadding} 0px ${listPadding} 8px`,
                    color: option.status
                      ? `${translateStatus[option.status].colorName}.text`
                      : undefined
                  }}
                  align="right"
                  size="small"
                >
                  <Box
                    sx={{
                      textWrap: 'nowrap',
                      fontWeight: 'bold',
                      fontSize
                    }}
                  >
                    {parseValue(option.value)} {option.unit}
                  </Box>
                </TableCell>
                {showComparativePeriod ? (
                  <TableCell
                    className="ComparativeLabel"
                    scope="row"
                    sx={{
                      width: '0px',
                      whiteSpace: 'normal',
                      minWidth: 0,
                      padding: `${listPadding} 0px ${listPadding} 4px`,
                      fontSize,
                      textDecoration: 'none'
                    }}
                    align="right"
                    size="small"
                  >
                    <TrendText
                      equivalentFactor={option.equivalentFactor}
                      difference={option.difference}
                      increaseIsPositive={option.increaseIsPositive}
                      numberOfDecimals={option.numberOfDecimals || 0}
                      significantFigures={option.significantFigures}
                      percentage={option.percentage || 0}
                      unitPrefix={option.unitPrefixSelected}
                    />
                  </TableCell>
                ) : null}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default ListContainer
